import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type { CursorPage } from '@/shared/api/types';
import { REACTION_TYPES, type ReactionSummary, type ReactionType } from '@/shared/components/reactions';

import { feedKeys, type FeedComment, type Post, type StreamData } from '../api';

type CommentPages = InfiniteData<CursorPage<FeedComment>, string | null>;

/** Applies `fn` to every post of a stream; returning null removes the post. */
export function mapStream(
  data: StreamData | undefined,
  fn: (post: Post) => Post | null,
): StreamData | undefined {
  if (!data) return data;
  const pages: CursorPage<Post>[] = [];
  let changed = false;
  for (const page of data.pages) {
    const items: Post[] = [];
    let pageChanged = false;
    for (const post of page.items) {
      const next = fn(post);
      if (next !== post) pageChanged = true;
      if (next) items.push(next);
    }
    changed ||= pageChanged;
    pages.push(pageChanged ? { ...page, items } : page);
  }
  return changed ? { ...data, pages } : data;
}

/** Puts a new post on top of the first page (once). */
export function prependToStream(data: StreamData | undefined, post: Post): StreamData | undefined {
  if (!data || data.pages.length === 0) return data;
  if (data.pages.some((page) => page.items.some((p) => p.id === post.id))) return data;
  const [first, ...rest] = data.pages as [CursorPage<Post>, ...CursorPage<Post>[]];
  return { ...data, pages: [{ ...first, items: [post, ...first.items] }, ...rest] };
}

/** Updates a post wherever it is cached (streams, pinned lists, single post). */
export function updateCachedPost(queryClient: QueryClient, id: string, fn: (post: Post) => Post): void {
  queryClient.setQueriesData<StreamData>({ queryKey: feedKeys.streams }, (data) =>
    mapStream(data, (post) => (post.id === id ? fn(post) : post)),
  );
  queryClient.setQueriesData<Post[]>({ queryKey: feedKeys.pinnedAll }, (list) =>
    list?.some((p) => p.id === id) ? list.map((p) => (p.id === id ? fn(p) : p)) : list,
  );
  queryClient.setQueryData<Post>(feedKeys.post(id), (post) => (post ? fn(post) : post));
}

/** Replaces a post with a fresh copy from the server wherever it is cached. */
export function replaceCachedPost(queryClient: QueryClient, fresh: Post): void {
  updateCachedPost(queryClient, fresh.id, () => fresh);
}

/** Removes a post from every cache (deleted, or the reader lost access). */
export function removeCachedPost(queryClient: QueryClient, id: string): void {
  queryClient.setQueriesData<StreamData>({ queryKey: feedKeys.streams }, (data) =>
    mapStream(data, (post) => (post.id === id ? null : post)),
  );
  queryClient.setQueriesData<Post[]>({ queryKey: feedKeys.pinnedAll }, (list) =>
    list?.some((p) => p.id === id) ? list.filter((p) => p.id !== id) : list,
  );
  queryClient.removeQueries({ queryKey: feedKeys.post(id), exact: true });
  queryClient.removeQueries({ queryKey: feedKeys.comments(id) });
}

/**
 * Merges reaction counts from a realtime event into the reader's summaries: counts come from the server, "mine"
 * stays as the reader knew it unless the reader is the one who reacted (then it follows `actorReaction`).
 */
export function mergeReactionCounts(
  current: readonly ReactionSummary[],
  counts: readonly { type: ReactionType; count: number }[],
  actorIsMe: boolean,
  actorReaction: ReactionType | null,
): ReactionSummary[] {
  const mine = actorIsMe ? actorReaction : (current.find((r) => r.reactedByMe)?.type ?? null);
  return counts
    .filter((c) => c.count > 0)
    .map((c) => ({ type: c.type, count: c.count, reactedByMe: c.type === mine }))
    .sort((a, b) => REACTION_TYPES.indexOf(a.type) - REACTION_TYPES.indexOf(b.type));
}

/** The reaction the reader has now (after an optimistic toggle), to choose between "set" and "remove". */
export const myReaction = (reactions: readonly ReactionSummary[] | undefined): ReactionType | null =>
  reactions?.find((r) => r.reactedByMe)?.type ?? null;

/**
 * Seeds the comment threads of posts with the latest comments that came with them, so cards render their comments
 * without a request each (called from the query functions that load posts). Existing threads are kept.
 */
export function seedCommentThreads(queryClient: QueryClient, posts: readonly Post[]): void {
  for (const post of posts) {
    const preview = feedKeys.commentThread(post.id, 'preview');
    if (!queryClient.getQueryData(preview)) {
      queryClient.setQueryData<CommentPages>(preview, { pages: [post.latestComments], pageParams: [null] });
    }
    const full = feedKeys.commentThread(post.id, 'full');
    if (post.latestComments.nextCursor === null && !queryClient.getQueryData(full)) {
      queryClient.setQueryData<CommentPages>(full, { pages: [post.latestComments], pageParams: [null] });
    }
  }
}
